import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import api from "../../../services/api";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import toastOptions from "../../../utils/toastOptions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan, faCheck, faCircleExclamation, faDownload, faPrint } from "@fortawesome/free-solid-svg-icons";
import states from "../../../assets/br.citys.json";
import { utils } from "../../../utils/utils";
import DialogConfirm from "../../../components/DialogConfirm";
import Modal from "../../../components/modal";
import TablePagination from "../../../components/TablePagination";
import { TypeHistory } from "../../../models/enums/TypeHistory";
import { StatusType } from "../../../models/enums/StatusType";
import moment from "../../../utils/momentConfig";

interface Params {
  id: number;
  name: string;
  open: boolean;
  afterHandleSubmit: any;
  afterCloseModal: any;
  type?: TypeHistory;
}

enum SignTypePerson {
  OTHER = "OUTRO"
}

interface IssuedLetterDTO {
  id: number;
  additionalInfo: string;
  createdDate: Date;
  createdBy: string;
  member: {
    id: number;
    nameAndFunction: string;
    name: string;
    congregation: any;
  };
  note?: string;
  reason: TypeHistory;
  idHistoryClosedOn?: number;
  modifiedDate?: Date;
  status: StatusType;
  idMember: number;
  token: string;
  idHistoryConfirm: number;
}

export default function MemberLetter(props: Params) {
  const {
    id,
    name,
    open,
    afterHandleSubmit,
    afterCloseModal,
  } = props;

  const [modalIsOpen, setModelOpen] = useState<boolean>(false);
  const [cities, setCities] = useState<string[]>([]);
  const [isOtherPerson, setOtherPerson] = useState<boolean>(false);
  const [openConfirmation, setConfirmationOpen] = useState<boolean>(false);
  const [letter, setLetter] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRecommendationLetter, setIsRecommendationLetter] = useState<boolean>(false);
  const [isChangeLetter, setIsChangeLetter] = useState<boolean>(false);
  const [reason, setReason] = useState<TypeHistory>();
  const [currentIssuedLetter, setCurrentIssuedLetter] = useState<IssuedLetterDTO>();
  const [openIssuedCancel, setOpenIssuedCancel] = useState<boolean>(false);
  const [actionLetterId, setActionLetterId] = useState<number>(0);
  const [issuedTableRefresh, setIssuedTableRefresh] = useState<boolean>(false);
  const [hasIssuedLetters, setHasIssuedLetters] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isInitialLettersLoaded, setIsInitialLettersLoaded] = useState<boolean>(false);
  const isDisabled = () => isLoading || (isChangeLetter && hasIssuedLetters);

  const schema = yup.object().shape({
    state: yup.string(),
    city: yup.string().when('state', {
      is: (value) => value != null && value != '',
      then: yup.string().required("Estado informado, Cidade obrigatória"),
    }),
    signPerson: yup.string()
      .when([], {
        is: () => isRecommendationLetter,
        then: (schema: any) => schema.required('Quem irá assinar é obrigatório'),
        otherwise: (schema: any) => schema.notRequired()
    }),
    otherSignPerson: yup.string().when('signPerson', {
      is: isRecommendationLetter && SignTypePerson.OTHER,
      then: yup.string().required("Quem irá assinar é obrigatório")
    }),
    note: yup.string(),
    reason: yup.string()
      .when([], {
        is: () => !isRecommendationLetter,
        then: (schema: any) => schema.required('Motivo da Carta é obrigatório'),
        otherwise: (schema: any) => schema.notRequired()
    })
  });

  const { register, handleSubmit, reset, clearErrors, errors, formState } = useForm({ resolver: yupResolver(schema) });

  useEffect(() => {
    setModelOpen(open);
    setIsRecommendationLetter(props.type === TypeHistory.RECOMMENDATION_LETTER);
    setIsChangeLetter(props.type === TypeHistory.CHANGE_LETTER);
  }, [id, open, props.type]);

  // For initialize table data when open modal with issued letters
  useEffect(() => {
    if (!open) return;

    setIsInitialLettersLoaded(false);
    setHasIssuedLetters(false);
    setIssuedTableRefresh((prev) => !prev);
    void fetchIssuedLetters(1, 10, true);
    dialogClose();
  }, [open]);

  const fetchIssuedLetters = async (page: number, limit: number, shouldFinalizeInitialLoad: boolean = false) => {
    try {
      setLoading(true);
      
      const response = await api.get("letters/issued", {
        params: {
          page: page,
          limit: limit,
          type: props.type,
          idMember: id
        }
      });

      setHasIssuedLetters(response.data.total > 0);

      return response;
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
      return { data: [], total: 0 };
    } finally {
      setLoading(false);
      if (shouldFinalizeInitialLoad) {
        setIsInitialLettersLoaded(true);
      }
    }
  };

  const closeModal = () => {
    handleCloseModal();
    afterCloseModal();
  };

  const handleCloseModal = () => {
    setModelOpen(false);
    afterCloseModal();
  };

  const selectCities = (state: string) => {
    setCities([]);
    const citiesFromState = states
      .filter((s) => s.initial === state)
      .shift()?.cities;
    if (citiesFromState) {
      setCities(citiesFromState);
    }
  };

  const handleSignPerson = (signPersonSelected: string) => {
    setOtherPerson(signPersonSelected == SignTypePerson.OTHER);
    clearErrors();
  }

  const onSubmit = async (request: any) => {
    try {
      setIsLoading(true);  
      request.idMember = id;
      request.type = props.type;

      if (!isRecommendationLetter) {
        request.signPerson = utils.standardLetterSignPerson();
      }

      setLetter(request);

      const { data } = await api.post(`letters/member/${id}`, request);
    
      const nameLetter = `${name.toLocaleLowerCase().split(' ')[0]}-carta-${isRecommendationLetter ? 'recomendação' : 'mudança'}-${new Date().getTime()}.pdf`;

      utils.printPDF(nameLetter, data.item.report);

      setLetter((prevLetter: any) => ({
        ...prevLetter,
        token: data.item.token,
        id: data.item.id
      }));

      if (isChangeLetter) {
        setConfirmationOpen(true);

        setCurrentIssuedLetter({
          id: data.item.id,
          additionalInfo: data.item.token,
          idMember: id,
          reason: request.reason
        } as IssuedLetterDTO);
      }
      setIssuedTableRefresh((prev) => !prev);
      setHasIssuedLetters(true);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleProcessLetter = async (confirmationCode: string) => {
    try {
      setConfirmationOpen(false);
      setIsLoading(true);
      
      if (currentIssuedLetter) {
        if (!confirmationCode) {
          toast.warning("Informe a senha", toastOptions);
          return;
        }
        setActionLetterId(currentIssuedLetter.id);
        
        const payload: any = {
          id: currentIssuedLetter.id,
          token: currentIssuedLetter.additionalInfo,
          type: currentIssuedLetter.reason,
          idMember: currentIssuedLetter.idMember,
          confirmationCode: confirmationCode
        };

        await api.post(`letters/member/${currentIssuedLetter.id}/response`, payload);

        toast.success("Carta confirmada com sucesso!", toastOptions);
        dialogClose();
        setIssuedTableRefresh((prev) => !prev);
        handleCloseModal();
        afterHandleSubmit(letter.type);
      }
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      setIsLoading(false);
      setActionLetterId(0);
    }
  }

  const dialogClose = () => {
    setConfirmationOpen(false);
    setCurrentIssuedLetter(undefined);
    setActionLetterId(0);
  }

  const handleDownloadIssuedLetter = async (row: IssuedLetterDTO) => {
    try {
      setActionLetterId(row.id);

      const request: any = {
        id: row.id,
        idMember: row.member?.id,
        type: row.reason,
        note: row.note,
        createdDate: row.createdDate,
      };

      if (row.reason !== TypeHistory.RECOMMENDATION_LETTER) {
        request.signPerson = utils.standardLetterSignPerson();
      }

      const { data } = await api.post(`letters/member/${row.member?.id}`, request);

      const nameLetter = `${row.member?.name.toLocaleLowerCase().split(" ")[0]}-carta-${row.reason === TypeHistory.RECOMMENDATION_LETTER ? "recomendação" : "mudança"}-${new Date().getTime()}.pdf`;
      utils.printPDF(nameLetter, data.item.report);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      setActionLetterId(0);
    }
  };

  const openConfirmIssuedLetter = (row: IssuedLetterDTO) => {
    setCurrentIssuedLetter(row);
    setConfirmationOpen(true);
  };

  const openCancelIssuedLetter = (row: IssuedLetterDTO) => {
    setCurrentIssuedLetter(row);
    setOpenIssuedCancel(true);
  };

  const closeCancelIssuedLetter = () => {
    setOpenIssuedCancel(false);
    setCurrentIssuedLetter(undefined);
  };

  const handleCancelIssuedLetter = async () => {
    try {
      setOpenIssuedCancel(false);

      if (!currentIssuedLetter) {
        return;
      }

      setActionLetterId(currentIssuedLetter.id);
      await api.delete(`letters/${currentIssuedLetter.id}`);
      toast.success("Carta cancelada com sucesso!", toastOptions);
      closeCancelIssuedLetter();
      setIssuedTableRefresh((prev) => !prev);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      setActionLetterId(0);
    }
  };

  const issuedLettersColumns = [
    {
      name: "Tipo",
      sortable: true,
      grow: 2,
      cell: (row: IssuedLetterDTO) => <span>{row.reason}</span>
    },
    {
      name: "Emitida em",
      sortable: true,
      cell: (row: IssuedLetterDTO) => {
        const isCanceled = row.status === StatusType.INACTIVE;
        const isConfirmed = !!row.idHistoryClosedOn || !!row.idHistoryConfirm || row.reason === TypeHistory.RECOMMENDATION_LETTER;
        const isExpired = moment().diff(moment(row.createdDate), "days") > 30;

        return <div className="uk-flex uk-flex-column">
          <span>{moment(row.createdDate).format("DD/MM/yyyy HH:mm")}</span>
          <span className={`uk-text-nowrap uk-badge ${isCanceled ? 'uk-badge-danger' : isExpired ? 'uk-badge-danger' : isConfirmed ? 'uk-badge-success' : 'uk-badge-warning'}`}>
            {isCanceled ? 'Cancelada' : (row.reason == TypeHistory.RECOMMENDATION_LETTER && isExpired) ? 'Vencida' : isConfirmed ? 'Confirmada' : 'Confirmação Pendente'}
          </span>
        </div>
      }
    },
    {
      name: "Ações",
      button: true,
      sortable: false,
      cell: (row: IssuedLetterDTO) => {
        const isProcessing = actionLetterId === row.id;
        const isCanceled = row.status === StatusType.INACTIVE;
        const isConfirmed = (row?.idHistoryClosedOn ?? 0) > 0 || (row?.idHistoryConfirm ?? 0) > 0 || row.reason === TypeHistory.RECOMMENDATION_LETTER;
        const isExpired = moment().diff(moment(row.createdDate), "days") > 30;

        if (isProcessing) {
          return <span uk-spinner="true; ratio:0.5"></span>;
        }

        return (
          <div className="uk-text-nowrap">
            <a
              href="#"
              className="uk-margin-small-right"
              uk-tooltip="title: Baixar carta"
              onClick={(ev) => {
                ev.preventDefault();
                handleDownloadIssuedLetter(row);
              }}
            >
              <FontAwesomeIcon icon={faDownload} />
            </a>

            {!isCanceled && !isConfirmed && !(row.reason == TypeHistory.RECOMMENDATION_LETTER && isExpired) && (
              <a
                href="#"
                className="uk-margin-small-right"
                uk-tooltip="title: Confirmar carta"
                onClick={(ev) => {
                  ev.preventDefault();
                  openConfirmIssuedLetter(row);
                }}
              >
                <FontAwesomeIcon icon={faCheck} />
              </a>
            )}

            {!isCanceled && !isExpired && (
              <a
                href="#"
                uk-tooltip="title: Cancelar carta"
                onClick={(ev) => {
                  ev.preventDefault();
                  openCancelIssuedLetter(row);
                }}
              >
                <FontAwesomeIcon icon={faBan} />
              </a>
            )}
          </div>
        );
      }
    }
  ];

  if (!props.type) return <></>;

  return (
    <>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        className="modal-small"
        shouldCloseOnOverlayClick={false}
      >
        <div className="uk-modal-header">
          <h2 className="uk-modal-title">Emissão de Carta</h2>
        </div>
        <button
          className="uk-modal-close-default"
          uk-close="true"
          type="button"
          onClick={() => handleCloseModal()}
        />
        <form onSubmit={handleSubmit<any>(onSubmit)} onReset={reset}>
          <div className="uk-modal-body">
            <div className="uk-grid-small" uk-grid="true">                
                <div className="uk-width-1@s">
                  <div
                    className="uk-card uk-card-default uk-card-small uk-padding-small uk-flex uk-flex-between uk-flex-middle"
                    style={{ borderLeft: "4px solid #1e87f0", background: "linear-gradient(90deg, #f8fbff 0%, #ffffff 100%)" }}
                  >
                    <div>
                      <div className="uk-text-meta uk-text-uppercase uk-text-bold">Emissão de Documento</div>
                      <h4 className="uk-margin-remove">Carta de {isRecommendationLetter ? 'Recomendação' : 'Mudança'}</h4>
                      <div className="uk-text-small uk-margin-small-top">
                        <span className="uk-text-bold">{name}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="uk-width-1@m uk-margin-small-top">
                  <div className="uk-text-small uk-text-bold uk-margin-small-bottom">Cartas emitidas</div>
                  {!hasIssuedLetters && !loading && (
                    <div className="uk-text-small uk-text-muted">Nenhuma carta emitida até o momento.</div>
                  )}
                  {hasIssuedLetters && (
                    <>
                      {!loading && isChangeLetter && hasIssuedLetters &&
                        <div className="uk-width-1@s uk-margin-small-bottom">
                          <span className="uk-alert-warning uk-display-block uk-padding-small">
                            Já existe carta emitida para este membro. Não é possível emitir uma nova enquanto houver carta ativa ou pendente.
                          </span>
                        </div>
                      }

                      <TablePagination
                        columns={issuedLettersColumns}
                        fetchData={fetchIssuedLetters}
                        refresh={issuedTableRefresh}
                      />
                    </>
                  )}
                </div>

                {!isInitialLettersLoaded ? (
                  <div className="uk-width-1@m uk-flex uk-flex-center uk-margin-small-top">
                    <div uk-spinner="true; ratio:1.2"></div>
                  </div>
                ) : (
                  <>
                    {isChangeLetter && <>
                      {!isDisabled() &&
                        <span className="uk-text-small uk-text-light uk-text-danger uk-width-1@s uk-margin-small-bottom">
                          <FontAwesomeIcon icon={faCircleExclamation} className="uk-margin-small-right" /> 
                          Atenção!<br/>
                          <ul className="uk-margin-remove-top">
                            <li>Nesta tela irá fazer a impressão e ao [Confirmar]:</li>
                            <li>O pastor presidente e o secretário geral serão notificados, via e-mail, para assinarem a carta fisicamente</li>
                            <li>O membro ficará <b>ATIVO</b> até que faça a [Confirmação] na tela de Secretaria/Cartas Emitidas</li>
                            <li>O processo pode levar até 7 dias, devido a agenda do pastor e do secretário geral</li>
                          </ul>
                        </span>
                      }
                      <div className="uk-width-1-3@s">
                        <label className="uk-form-label" htmlFor="reason">Motivo da Carta</label>
                        <div uk-form-custom="target: > * > span:first-child" className="uk-width-1">
                          <select className="uk-form-small"
                            id="reason" 
                            name="reason"
                            defaultValue={""}
                            ref={register}
                            disabled={isDisabled()}
                            onChange={(el) => setReason(el.target.value as TypeHistory)}
                          >
                            <option value="" key={`reason0`}>Selecione...</option>
                            <option value={TypeHistory.CHANGE_CHURCH} key={`reason1`}>{TypeHistory.CHANGE_CHURCH}</option>
                            <option value={TypeHistory.CHANGE_CITY} key={`reason2`}>{TypeHistory.CHANGE_CITY}</option>
                            <option value={TypeHistory.OTHER} key={`reason3`}>{TypeHistory.OTHER}</option>
                          </select>
                          <button 
                            className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                            style={{textTransform: 'none'}} 
                            type="button" 
                            tabIndex={-1}
                            disabled={isDisabled()}
                          >
                            <span></span>
                            <span uk-icon="icon: chevron-down"></span>
                          </button>
                        </div>
                        <small className="uk-text-danger">{errors.reason?.message}</small>
                      </div>
                    </>}
                    {isRecommendationLetter && <div className="uk-width-1@s">
                      <div className="uk-form-controls uk-flex uk-flex-column">
                        <span className="uk-text-small uk-text-light uk-text-italic uk-margin-small-bottom">
                          A assinatura do <span className="uk-text-bold">Pastor Presidente</span> constará na carta digital                      
                        </span>
                        <label className="uk-form-label" htmlFor="signPerson">Quem vai assinar como emissor?</label>
                        <div uk-form-custom="target: > * > span:first-child" className="uk-width-1">
                          <select className="uk-form-small"
                              id="signPerson" 
                              name="signPerson"
                              defaultValue={""}
                              ref={register}
                              onChange={(el) => handleSignPerson(el.target.value)}
                          >
                              <option value="" key={`signPerson0`}>Selecione...</option>
                              <option value="Pr. Samuel José Alves / 1º Secretário / CGADB 95.546" key={`signPerson1`}>
                                {utils.standardLetterSignPerson()}
                              </option>
                              <option value="Pr. Frederico Martins Alves da Silva / 2º Secretário / CGADB 109.379" key={`signPerson2`}>
                                Pr. Frederico Martins Alves da Silva / 2º Secretário / CGADB 109.379
                              </option>
                              <option value="Coop. Guilherme A. de Oliveira Boccia / Secretário Adjunto" key={`signPerson3`}>
                                Coop. Guilherme Augusto de Oliveira Boccia / Secretário Adjunto
                              </option>                          
                              <option value={SignTypePerson.OTHER} key={`signPerson4`}>Outro</option>
                          </select>
                          <button 
                              className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                              style={{textTransform: 'none'}} 
                              type="button" 
                              tabIndex={-1}
                          >
                              <span></span>
                              <span uk-icon="icon: chevron-down"></span>
                          </button>
                        </div>
                      </div>
                      <small className="uk-text-danger">{errors.signPerson?.message}</small>
                    </div>}
                    {
                      isOtherPerson && (
                        <div className="uk-width-1@s">
                          <div className="uk-form-controls">
                            <label className="uk-form-label" htmlFor="otherSignPerson">
                              Especifique o nome e a função separado por /. Ex: Pr. Beltrano / Dirigente <br />
                              <FontAwesomeIcon icon={faCircleExclamation} className="uk-margin-small-right uk-text-primary" />
                              Neste caso, será necessário imprimir e assinar fisicamente a carta
                            </label>
                            <div className="uk-form-controls">
                              <input type="text" 
                                id="otherSignPerson" 
                                name="otherSignPerson" 
                                className="uk-input uk-form-small"
                                autoComplete={"off"}
                                ref={register}
                              />
                            </div>
                          </div>
                          <small className="uk-text-danger">{errors.otherSignPerson?.message}</small>
                        </div>      
                      )
                    }
                    {(isRecommendationLetter || reason === TypeHistory.CHANGE_CITY) && <>
                      <div className="uk-width-1-3@s">
                        <label className="uk-form-label" htmlFor="state">
                          Estado destino
                        </label>
                        <div
                          uk-form-custom="target: > * > span:first-child"
                          className="uk-width-1"
                        >
                          <select
                            className="uk-form-small"
                            id="state"
                            name="state"
                            ref={register}
                            onChange={(el) => selectCities(el.target.value)}
                          >
                            <option value="" key="state0">
                              Selecione...
                            </option>
                            {states.map((f) => {
                              return (
                                <option value={f.initial} key={`state${f.initial}`}>
                                  {f.name}
                                </option>
                              );
                            })}
                          </select>
                          <button
                            className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                            style={{ textTransform: "none" }}
                            type="button"
                            tabIndex={-1}
                          >
                            <span></span>
                            <span uk-icon="icon: chevron-down"></span>
                          </button>
                        </div>
                        <small className="uk-text-danger">
                          {errors.state?.message}
                        </small>
                      </div>
                      <div className="uk-width-1-3@s">
                        <label className="uk-form-label" htmlFor="city">
                          Cidade destino
                        </label>
                        <div
                          uk-form-custom="target: > * > span:first-child"
                          className="uk-width-1"
                        >
                          <select
                            className="uk-form-small"
                            id="city"
                            name="city"
                            ref={register}
                            disabled={cities.length === 0}
                          >
                            <option value="" key={`city0`}>
                              Selecione...
                            </option>
                            {cities.map((c) => {
                              return (<option value={c} key={c}>{c}</option>);
                            })}
                          </select>
                          <button
                            className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                            style={{ textTransform: "none" }}
                            type="button"
                            tabIndex={-1}
                            disabled={cities.length === 0}
                          >
                            <span></span>
                            <span uk-icon="icon: chevron-down"></span>
                          </button>
                        </div>
                        <small className="uk-text-danger">
                          {errors.city?.message}
                        </small>
                      </div>              
                    </>}
                    <div className="uk-width-1@s">
                      <label className="uk-form-label" htmlFor="note">
                        Observações
                      </label>                
                      <textarea
                        id="note"
                        name="note"
                        className="uk-textarea uk-form-small"
                        rows={4}
                        style={{ resize: "none" }}
                        defaultValue={""}
                        autoComplete={"off"}
                        ref={register}
                        disabled={isDisabled()}
                      />
                      <label className="uk-text-small uk-text-light uk-text-italic">
                        Ao inserir algum valor aqui, ele virá imediamente após os dizeres iniciais: <br/> 
                        Observamos que [sua observação]
                      </label>
                      <small className="uk-text-danger">{errors.note?.message}</small>
                    </div>
                  </>
                )}
              </div>
            </div>
          <div className="uk-modal-footer">
            <div
              className="uk-grid-small uk-child-width-1-4@s uk-flex-center uk-text-center"
              uk-grid="true"
            >
              <button
                className="uk-button uk-button-default uk-button-small uk-modal-close uk-margin-left"
                onClick={() => handleCloseModal()}
                disabled={formState.isSubmitting}
                type="button"
              >
                Cancelar
              </button>
              <button
                disabled={(formState.isSubmitting || isDisabled())}
                className="uk-button uk-button-primary uk-button-small uk-margin-small-left"
                type="submit"
              >
                {(formState.isSubmitting || isLoading) && <div uk-spinner="true; ratio:0.5"></div>}
                Imprimir
                <FontAwesomeIcon className="uk-margin-small-left" icon={faPrint} />
              </button>
            </div>
          </div>
        </form>
      </Modal>
      
      <DialogConfirm
        open={openConfirmation}
        text={[
          `A Carta de ${isRecommendationLetter ? 'Recomendação' : 'Mudança'} para ${name} foi impressa corretamente?`,
          `Se sim clique em [Confirmar], se não clique em [Cancelar] e tente novamente.`
        ]}
        title={`Carta de ${isRecommendationLetter ? 'Recomendação' : 'Mudança'} Impressa?`}
        handleConfirmation={handleProcessLetter}
        afterClose={dialogClose}
        confirmTextLabel={isChangeLetter ? "Informe sua senha para confirmar" : undefined}
      ></DialogConfirm>

      <DialogConfirm
        open={openIssuedCancel}
        text={[
          `Deseja cancelar esta carta emitida?`,
          `Essa ação marcará a carta como cancelada.`
        ]}
        title="Cancelar Carta Emitida"
        handleConfirmation={handleCancelIssuedLetter}
        afterClose={closeCancelIssuedLetter}
      ></DialogConfirm>
    </>
  );
}